import React, { useEffect, useState } from 'react';
import { TCustomer } from 'app/Pages/Clients/common';
import {
  ButtonAddItem,
  ContactsControls,
  ContactsControlsButton,
  ContactsItemsWrapper,
  ContactsRemoveIcon,
  ContactsItemWrapper,
  ContactsStyled,
  IconPlus,
} from './styles';
import {
  JSONSchema,
  TAddress,
  TAddressType,
  TContactPerson,
  JSONSchemaProp,
  TCustomerContact,
  TZipCodeResult, TError,
} from 'models';
import { Input, FormGroup, InputOption } from '@insly/qmt-reactjs-ui-lib';
import apiConfig, { DEFAULT_RESIDENT_COUNTRY } from 'config/api';
import axios, { AxiosError, AxiosPromise } from 'axios';
import {
  createTempId,
  preflightAPICall,
  translateTitleInScheme,
  zipCodeMatcher,
  fetchZipCodeData,
  setStreets,
  concurrentRequestsResolver,
  processResponseErrors,
  translateEnumItemInScheme,
  errorNotification,
  translate,
  filterStreets,
  fetchCityData,
  processAPIError,
  setRules,
  validate,
  cloneObject,
  customerEmailValidation,
  filterStreetsWithoutCity,
  customerPhoneValidation,
  addCountryCode
} from 'utils';
import { useScheme } from 'context/Schema';
import LoadingOverlay from 'helpers/LoadingOverlay';
import { renderErrors } from 'app/Components/common/Error';
import { useApp } from 'context/App';
import { ContactsDataBlock, ContactsDataBlockTitle, ContactsItem, ContactPersonRow } from 'styles/common';
import {
  ADDRESS_TYPES,
  fillAddressFieldsFromResults,
  fillAddressFieldsWithDefaultData
} from 'app/Components/common/AddressForm';
import { TUserContact } from '@insly/qmt-reactjs-ui-lib/dist/models';
import { IFormGroupItem } from '@insly/qmt-reactjs-ui-lib/dist/components/FormGroup';
import { createContactFormFields } from 'app/Components/common/Contacts';
import { getLatestUserRevision } from 'app/Components/common/Customer';
import { CUSTOMER_CONTACT_VALIDATION_FIELDS } from 'config/itemLists';
import { ADDRESS_DEFAULT } from 'app/Pages/Clients/consts';
import {
  getAddedTypes,
  setAddressTypeOptions,
  getVisibleAddressesLength,
  getParentsAddedTypes
} from 'app/Pages/Quote/Profile/body/Participants/utils';

const API_URL_ADDRESSES = `${apiConfig.CUSTOMER}/customers/%USER_ID%/addresses`;
const API_URL_CONTACTS = `${apiConfig.CUSTOMER}/customers/%USER_ID%/contacts`;
const API_URL_CONTACT_PERSONS = `${apiConfig.CUSTOMER}/customers/%USER_ID%/contact-persons`;

let CancelToken = axios.CancelToken;
let cancelTokenSource = CancelToken.source();

const SEARCH_DELAY = 400;
const MIN_VALUE_LENGTH_TO_START_SEARCH = 3;
const REVISION_ERROR_CODE = 'CS00017';
let searchTimeout: ReturnType<typeof setTimeout>;

interface IContacts {
  profile: TCustomer,
  onSaveUser: (user: TCustomer) => void,
  customerContactsSchema: JSONSchemaProp,
  transparent?: boolean,
  readOnly?: boolean,
  isClients?: boolean,
  handleDrawerClose?: () => void,
}

const Contacts = ({ profile, onSaveUser, customerContactsSchema, transparent, isClients, handleDrawerClose, readOnly }: IContacts) => {
  let schema = useScheme();
  const { showNotification } = useApp();

  const [isLoading, updateIsLoading] = useState(false);

  useEffect(() => {
    if (!schema.customerSchemaNetworkStatus || schema.customerSchemaNetworkStatus === 'failed') {
      schema.getCustomerSchemas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSaveContactPersons = (contactPersons: TCustomer['contact_persons']) => {
    getLatestUserRevision(profile.id as string, (data) => {
      onSaveUser({
        ...data
      });
      updateIsLoading(false);
    }, (error) => {
      errorNotification(error, showNotification, false, 'quote');
      updateIsLoading(false);
    });
  };

  const onSaveContacts = (contacts: TCustomer['contacts']) => {
    getLatestUserRevision(profile.id as string, (data) => {
      onSaveUser({
        ...data
      });
      updateIsLoading(false);
    }, (error) => {
      errorNotification(error, showNotification, false, 'quote');
      updateIsLoading(false);
    });
  };

  const onSaveAddresses = (addresses: TCustomer['addresses']) => {
    getLatestUserRevision(profile.id as string, (data) => {
      onSaveUser({
        ...data
      });
      updateIsLoading(false);
    }, (error) => {
      errorNotification(error, showNotification, false, 'quote');
      updateIsLoading(false);
    });
  };

  const handleSaveUserAddress = (addresses: TAddress[], cb: (status: string, data?: Record<string, unknown>[]) => void) => {
    preflightAPICall(() => {
      const results: Record<string, unknown>[] = [];

      const sendRequestSequentially = (index: number) => {
        if (index >= addresses.length) {
          cb('ok', results);
          return;
        }

        const a = addresses[index];

        if (a.props.same_is && a.props.same_is !== 'other') {
          sendRequestSequentially(index + 1);
          return;
        }

        const preparedProps = { ...a.props, room: a.props.room || undefined };
        delete preparedProps.same_is;

        let requestPromise;

        if (a.id && !a.id.includes('TEMP__') && !a.deleted) {
          requestPromise = axios.patch(
            `${API_URL_ADDRESSES.replace('%USER_ID%', profile.id as string)}/${a.id}`,
            {
              id: a.id,
              props: { ...preparedProps },
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );
        } else if (a.deleted && !a.id.includes('TEMP__')) {
          requestPromise = axios.delete(`${API_URL_ADDRESSES.replace('%USER_ID%', profile.id as string)}/${a.id}`);
        } else if (!a.deleted) {
          requestPromise = axios.post(
            API_URL_ADDRESSES.replace('%USER_ID%', profile.id as string),
            {
              props: { ...preparedProps },
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );
        }

        if (requestPromise) {
          requestPromise
            .then((response) => {
              results.push({ ...response.data, id: a.id });
              sendRequestSequentially(index + 1);
            })
            .catch((error) => {
              results.push({ error, id: a.id });
              sendRequestSequentially(index + 1);
            });
        } else {
          sendRequestSequentially(index + 1);
        }
      };

      sendRequestSequentially(0);
    });
  };

  const handleSaveUserContact = (contacts: TCustomer['contacts'], cb: (status: string, data?: Record<string, unknown>[]) => void) => {
    preflightAPICall(() => {

      const requests: AxiosPromise[] = [];

      const itemsToSendIds: string[] = [];

      contacts && contacts.forEach(contact => {

        if (!contact.touched) {
          return;
        }

        if (contact.id && !contact.id.includes('TEMP__') && !contact.deleted) {
          itemsToSendIds.push(contact.id);
          requests.push(
            axios.patch(`${API_URL_CONTACTS.replace('%USER_ID%', profile.id as string)}/${contact.id}`,
              {
                ...contact
              },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              })
          );
        } else if (contact.deleted) {
          itemsToSendIds.push(contact.id as string);
          requests.push(
            axios.delete(`${API_URL_CONTACTS.replace('%USER_ID%', profile.id as string)}/${contact.id}`)
          );
        } else {
          itemsToSendIds.push(contact.id as string);

          //delete temp id
          const contactPrepared = { ...contact };
          delete contactPrepared.id;

          requests.push(
            axios.post(API_URL_CONTACTS.replace('%USER_ID%', profile.id as string),
              {
                ...contactPrepared
              },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              })
          );
        }
      });

      const requestsResolved = concurrentRequestsResolver(requests);

      if (requestsResolved.length > 0) {
        axios.all(requestsResolved).then(axios.spread((...responses) => {
          cb('ok', responses.map((r, index) => (r ? {
            ...r,
            id: itemsToSendIds[index]
          } : {
            id: itemsToSendIds[index]
          })));
        })).catch(errors => {
          if (errors.response) {
            cb('error', errors.response.data);
          }
        });
      }

    });
  };

  const handleSaveUserContactPersons = (contactPersons: TContactPerson[], cb: (status: string, data?: Record<string, unknown>[]) => void) => {
    preflightAPICall(() => {
      const requests: AxiosPromise[] = [];

      const itemsToSendIds: string[] = [];

      contactPersons && contactPersons.forEach(contact => {

        const preparedContactData: {
          id?: string,
          preferred: boolean,
          props: {
            first_name: string,
            last_name: string,
            email: string,
            phone: string,
          },
        } = {
          preferred: contact.preferred,
          props: {
            first_name: contact.first_name,
            last_name: contact.last_name,
            email: contact.email,
            phone: (contact.phone && !contact.phone.startsWith(window._env_.DEFAULT_COUNTRY_CODE)) ? addCountryCode(contact.phone) : contact.phone,
          },
        };

        if (contact.id && !contact.id.includes('TEMP__')) {
          preparedContactData.id = contact.id;
        }

        if (contact.id && !contact.id.includes('TEMP__') && !contact.deleted) {
          itemsToSendIds.push(contact.id);
          requests.push(
            axios.patch(`${API_URL_CONTACT_PERSONS.replace('%USER_ID%', profile.id as string)}/${contact.id}`,
              {
                ...preparedContactData
              },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              })
          );
        } else if (contact.deleted && !contact.id.includes('TEMP__')) {
          itemsToSendIds.push(contact.id as string);
          requests.push(
            axios.delete(`${API_URL_CONTACT_PERSONS.replace('%USER_ID%', profile.id as string)}/${contact.id}`)
          );
        } else {
          itemsToSendIds.push(contact.id as string);
          requests.push(
            axios.post(API_URL_CONTACT_PERSONS.replace('%USER_ID%', profile.id as string),
              {
                ...preparedContactData
              },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              })
          );
        }
      });

      const requestsResolved = concurrentRequestsResolver(requests);

      if (requestsResolved.length > 0) {
        axios.all(requestsResolved).then(axios.spread((...responses) => {
          cb('ok', responses.map((r, index) => (r ? {
            ...r,
            id: itemsToSendIds[index]
          } : {
            id: itemsToSendIds[index]
          })));
        })).catch(errors => {
          if (errors.response) {
            cb('error', errors.response.data);
          }
        });
      }
    });
  };

  const handleError = (error: AxiosError) => {
    errorNotification(error, showNotification, false, 'quote');
  };

  return (
    <ContactsStyled>
      {isLoading ? <LoadingOverlay /> : null}
      {PhonesAndEmails(
        profile.contacts,
        handleSaveUserContact,
        onSaveContacts,
        updateIsLoading,
        customerContactsSchema,
        transparent,
        profile.props.resident,
        isClients,
        handleDrawerClose,
        readOnly
      )}

      {ContactPersons(profile.contact_persons,
        handleSaveUserContactPersons,
        onSaveContactPersons,
        schema.customerContactPersonSchema,
        updateIsLoading,
        transparent,
        isClients,
        handleDrawerClose,
        readOnly
      )}

      {Addresses(
        profile.addresses,
        profile.revision_id as string,
        handleSaveUserAddress,
        onSaveAddresses,
        schema,
        updateIsLoading,
        transparent,
        (profile.type === 'individual' && profile.props.resident === false),
        isClients,
        handleDrawerClose,
        readOnly,
        profile.id,
        handleError
      )}
    </ContactsStyled>
  );
};

const Addresses = (
  addresses: TCustomer['addresses'],
  profileRevisionId: string,
  handleSaveUserAddress: (addresses: TAddress[], cb: (status: string, data?: Record<string, unknown>[]) => void) => void,
  onSaveAddresses: (addresses: TAddress[]) => void,
  schema: Record<string, unknown>,
  updateIsLoading: (isLoading: boolean) => void,
  transparent?: boolean,
  isNotResident?: boolean,
  isClients?: boolean,
  handleDrawerClose?: () => void,
  readOnly?: boolean,
  customerId?: string,
  handleError?: (error: AxiosError) => void,
) => {

  const ADDRESS_SCHEMA = schema.customerAddressSchema as JSONSchema;

  const [form, updateForm] = useState<TAddress[]>([]);
  const [isTouched, updateIsTouched] = useState(false);

  const { showDialog } = useApp();

  const schemaProps = ADDRESS_SCHEMA.properties?.props?.properties as JSONSchema;

  const newAddressProps: TAddress['props'] = {
    ...cloneObject(ADDRESS_DEFAULT),
    same_is: 'other',
    country: isNotResident ? '' : DEFAULT_RESIDENT_COUNTRY,
  };

  const availableAddressTypesOptions = setAddressTypeOptions(schemaProps?.type?.items?.enum as TAddressType[], form, schemaProps?.type);
  const visibleAddressesLength = getVisibleAddressesLength(form);

  useEffect(() => {
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileRevisionId]);

  const handleSaveAddresses = (addresses: TAddress[]) => {
    addresses = addresses.map(address => ({
      id: address.id,
      props: {
        city: address.props.city,
        country: address.props.country,
        house: address.props.house,
        room: address.props.room,
        street: address.props.street,
        type: address.props.type,
        zip_code: address.props.zip_code,
        county: address.props.county,
        post:  address.props.post,
        commune: address.props.commune,
        region: address.props.region,
      }
    }));

    onSaveAddresses(addresses);
  };

  const resetForm = () => {
    addresses ? updateForm(JSON.parse(JSON.stringify(addresses))) : updateForm([]);
    updateIsTouched(false);
  };

  const handleChangeMultiple = (value: TAddress['props'], id: string) => {
    const touchedForm = [...form];
    const itemIndex = touchedForm.findIndex(i => i.id === id);

    if (itemIndex !== -1) {
      touchedForm[itemIndex].props = value;
      updateIsTouched(true);
      updateForm(touchedForm);
    }

  };

  const handleChange = (key: keyof TAddress['props'], value: string | string[], id: string, type?: TAddressType[], parent?: string) => {
    const touchedForm = [...form];
    const itemIndex = touchedForm.findIndex(i => i.id === id);
    //Handle change/remove parent address
    if (key === 'same_is') {
      const parentIndex = touchedForm.findIndex(i => i.id === parent);
      if (parentIndex !== -1) {
        const typeIndex = touchedForm[parentIndex].props.type.findIndex((t: TAddressType) => t === (type && type[0]));
        if (typeIndex !== -1) {
          touchedForm[parentIndex].props.type.splice(typeIndex, 1);
        }
      }
      if (value === 'other' && parent) {
        touchedForm.push({
          id: createTempId(),
          props: {
            ...newAddressProps,
            type: [...type as TAddressType[]],
          }
        });
      } else if (value !== 'other') {
        const newParentIndex = touchedForm.findIndex(i => i.props.type.includes(value as TAddressType));
        if (newParentIndex !== -1) {
          touchedForm[newParentIndex].props.type.push(...type as TAddressType[]);
        }
        if (itemIndex !== -1) {
          touchedForm.splice(itemIndex, 1);
        }
      } else {
        touchedForm[itemIndex].props.same_is = value as TAddressType;
      }
      // Handle all other address changes
    } else if (itemIndex !== -1) {
      touchedForm[itemIndex].error = [];
      if (key === 'type') {
        touchedForm[itemIndex].props[key][0] = value as TAddressType;
      } else if (key === 'zip_code' && typeof value === 'string') {
        touchedForm[itemIndex].props[key] = isNotResident ? value : zipCodeMatcher(value, touchedForm[itemIndex].props[key]);
      } else {
        touchedForm[itemIndex].props[key] = value as keyof TAddress['props'];
      }
    } else if (key === 'type' && parent) {
      const parentIndex = touchedForm.findIndex(i => i.id === parent);
      if (parentIndex !== -1) {
        if (type) {
          const parentTypeIndex = touchedForm[parentIndex].props.type.findIndex(i => i === type[0]);
          if (parentTypeIndex !== -1) {
            touchedForm[parentIndex].props.type.splice(parentTypeIndex, 1, value as TAddressType);
          }
        }
      }
    }
    updateIsTouched(true);
    updateForm(touchedForm);
  };

  const onAddItem = () => {
    const touchedForm = [...form];
    touchedForm.push({ id: createTempId(), props: { ...newAddressProps, type: [availableAddressTypesOptions[0].key] } });
    updateForm(touchedForm);
  };

  const onRemoveItem = (id: string, type?: TAddressType, parentId?: string) => {
    const touchedForm = [...form];

    if (parentId) {
      const parentIndex = touchedForm.findIndex(i => i.id === parentId);
      if (parentIndex !== -1) {
        const typeIndex = touchedForm[parentIndex].props.type.findIndex((t: TAddressType) => t === type);
        if (typeIndex !== -1) {
          touchedForm[parentIndex].props.type.splice(typeIndex, 1);
        }
      }
    } else {
      const itemIndex = touchedForm.findIndex(i => i.id === id);
      if (itemIndex !== -1) {
        if (touchedForm[itemIndex].id && !touchedForm[itemIndex].id.includes('TEMP')) {
          touchedForm[itemIndex].deleted = true;
        } else {
          touchedForm.splice(itemIndex, 1);
        }
      }
    }

    updateForm([...touchedForm]);

    // if Temp element was removed and no more elements left no need to call API
    updateIsTouched(!!touchedForm.length);
  };

  const handleRevisionError = () => {
    showDialog({
      icon: 'question',
      body: translate({ key: 'dialog.unsaved_changes.revision_issue' }),
      buttonAccept: translate({ key: 'common.save' }),
      buttonDecline: translate({ key: 'common.cancel' }),
      onDecline: () => handleDrawerClose && handleDrawerClose(),
      onAccept: () => getLatestUserRevision(
        customerId as string,
        (data) => processAddresses(data.addresses),
        (error) => {
          updateIsLoading(false);
          handleError && handleError(error);
        }),
      preventClose: true,
      style: {
        minWidth: '500px'
      }
    });
  };

  const processAddresses = (addresses?: TAddress[] | null) => {
    if (addresses?.length) {
      const oldAddresses = addresses.map(address => ({ ...address, deleted: true }));
      const touchedAddresses: TAddress[] = cloneObject(form);
      const filteredAddresses = touchedAddresses.filter(address => !address.deleted);
      const newAddresses: TAddress[] = filteredAddresses.map(address => ({ ...address, id: createTempId() }));

      handleSaveUserAddress([...oldAddresses, ...newAddresses], (status, data) => {
        if (status !== 'ok') {
          updateIsLoading(false);
        }

        // @ts-ignore
        processResponse(status, data, newAddresses, updateForm, updateIsTouched, handleSaveAddresses, updateIsLoading, handleDrawerClose);
      });
    }
  };

  const handleSaveData = () => {
    //@TODO: add validation
    updateIsLoading(true);

    handleSaveUserAddress([...form], (status, data) => {
      if (status !== 'ok') {
        updateIsLoading(false);
      }

      // @ts-ignore
      processResponse(status, data, cloneObject(form), updateForm, updateIsTouched, handleSaveAddresses, updateIsLoading, handleDrawerClose, handleRevisionError);
    });
  };

  return (
    <ContactsDataBlock isClients={isClients}>
      {transparent ? (
        <ContactsDataBlockTitle>
          {translate({ key: 'common.address' })}
        </ContactsDataBlockTitle>
      ) : null}
      <ContactsItemsWrapper>
        {form.map((item) => (
          [
            ...item.props.type.map((t: string, index: number) => item.deleted ? (item.error?.length && index === 0 ? renderErrors(item.error) : null)
              : renderAddress(
                {
                  ...item,
                  id: index === 0 ? item.id : `same_is_${item.id}_${t}`,
                  parent: index === 0 ? undefined : item.id as string,
                  props: {
                    ...item.props,
                    same_is: index === 0 ? 'other' : item.props.type[0],
                    type: index === 0 ? item.props.type : [item.props.type[index]]
                  }
                },
                handleChange,
                handleChangeMultiple,
                onRemoveItem,
                form.length,
                visibleAddressesLength,
                schemaProps,
                availableAddressTypesOptions,
                getParentsAddedTypes(form, item.props.type[0]),
                transparent,
                isNotResident,
                form,
                updateForm,
                readOnly
              ))
          ].filter(Boolean)
        ))}
        {schemaProps.type.items?.enum?.length ? (
          getAddedTypes(form).length < schemaProps.type.items.enum.length ? (
            <ButtonAddItem preset="secondary" onClick={onAddItem} disabled={readOnly}>
              <>
                {translate({ key: 'customer.contacts.add_address' })} <IconPlus icon="plus" />
              </>
            </ButtonAddItem>
          ) : null
        ) : null}
      </ContactsItemsWrapper>
      {isTouched ? renderControls(resetForm, handleSaveData, transparent, readOnly) : null}
    </ContactsDataBlock>
  );

};

// `transparent` marks the drawer usage: drawers get a single primary Save on the left, like the rest of the drawer
const renderControls = (resetForm: () => void, handleSaveData: () => void, transparent?: boolean, readOnly?: boolean) => (
  transparent ? (
    <ContactsControls className="contacts-save-controls">
      <ContactsControlsButton preset="primary" size="l" onClick={handleSaveData} disabled={readOnly}>
        {translate({ key: 'common.save' })}
      </ContactsControlsButton>
    </ContactsControls>
  ) : (
    <ContactsControls>
      <ContactsControlsButton preset="secondary" onClick={resetForm} disabled={readOnly}>
        {translate({ key: 'common.cancel' })}
      </ContactsControlsButton>
      <ContactsControlsButton onClick={handleSaveData} disabled={readOnly}>
        {translate({ key: 'common.save' })}
      </ContactsControlsButton>
    </ContactsControls>
  )
);

const renderAddress = (
  item: TAddress,
  handleChange: (key: keyof TAddress['props'], value: string, id: string, type?: TAddressType[], parentId?: string) => void,
  handleChangeMultiple: (value: TAddress['props'], id: string) => void,
  onRemoveItem: (id: string, type?: TAddressType, parentId?: string) => void,
  addressesLength: number,
  visibleAddressesLength: number,
  schemaProps: JSONSchema,
  availableAddressTypesOptions: { key: string, value: string }[],
  addedTypes: string[],
  transparent?: boolean,
  isNotResident?: boolean,
  addressForm?: TAddress[],
  updateAddressForm?: (address: TAddress[]) => void,
  readOnly?: boolean,
) => {

  const form = item.props;

  return (
    <ContactsItemWrapper key={item.id}>
      <ContactsItem>
        <AddressForm
          item={item}
          handleChange={handleChange}
          handleChangeMultiple={handleChangeMultiple}
          visibleAddressesLength={visibleAddressesLength}
          schemaProps={schemaProps}
          availableAddressTypesOptions={availableAddressTypesOptions}
          addedTypes={addedTypes}
          transparent={transparent}
          isNotResident={isNotResident}
          addressForm={addressForm}
          updateAddressForm={updateAddressForm}
          readOnly={readOnly}
        />
        {item.error?.length ? renderErrors(item.error) : null}
      </ContactsItem>
      {((visibleAddressesLength > 1 || item.parent) && !readOnly) ? (
        <ContactsRemoveIcon icon="trash" onClick={() => onRemoveItem(item.id as string, form.type[0], item.parent)} />
      ) : null}
    </ContactsItemWrapper>
  );
};

const AddressForm = ({
  item,
  handleChange,
  handleChangeMultiple,
  visibleAddressesLength,
  schemaProps,
  availableAddressTypesOptions,
  addedTypes,
  transparent,
  isNotResident,
  addressForm,
  updateAddressForm,
  readOnly,
} : {
  item: TAddress,
  handleChange: (key: keyof TAddress['props'], value: string, id: string, type?: TAddressType[], parentId?: string) => void,
  handleChangeMultiple: (value: TAddress['props'], id: string) => void,
  visibleAddressesLength: number,
  schemaProps: JSONSchema,
  availableAddressTypesOptions: { key: string, value: string }[],
  addedTypes: string[],
  transparent?: boolean,
  isNotResident?: boolean,
  addressForm?: TAddress[],
  updateAddressForm?: (address: TAddress[]) => void,
  readOnly?: boolean,
}) => {

  const { showNotification } = useApp();

  const [streets, updateStreets] = useState<{key: string, value: string}[]>([]);
  const [cities, updateCities] = useState<{key: string, value: string}[]>([]);
  const [isFetching, updateIsFetching] = useState(false);

  useEffect(() => {
    if (item.props?.zip_code?.length === 6 && !isNotResident) {
      updateIsFetching(true);
      fetchZipCodeData(
        item.props?.zip_code,
        (zipCodeResults) => {
          const streets = filterStreets(zipCodeResults, item.props.city);
          if (streets.length > 1) {
            setStreets(streets, updateStreets);
          }
          updateIsFetching(false);
        },
        updateCities,
        () => {},
        () => {},
        onError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onError = (error: AxiosError) => {
    updateIsFetching(false);
    showNotification({
      preset: 'error',
      message: processAPIError(error, false, true) as string,
      autoHide: true
    });
  };

  const setDefaultsAndUpdateZipCodeResults = (results: TZipCodeResult[]) => {
    if (results?.length > 1 && addressForm?.length && updateAddressForm) {
      const _addressForm = [...addressForm];
      const index = _addressForm.findIndex(address => address.id === item.id);

      _addressForm[index].props = {
        ..._addressForm[index].props,
        ...fillAddressFieldsWithDefaultData(results, updateStreets),
      };

      updateAddressForm(_addressForm);
    }

  };

  const form = item.props;
  const formGroupType: IFormGroupItem[] = [{
    type: 'select',
    options: {
      placeholder: schemaProps.type.placeholder,
      name: 'type',
      label: translateTitleInScheme(schemaProps.type),
      value: form.type[0],
      notFoundText: translate({ key: 'common.nothing_found' }),
      handleChange: (name, value) => handleChange(name as keyof TAddress['props'], value as string, item.id as string, form.type, item.parent),
      options: [{ key: form.type[0], value: translateEnumItemInScheme(schemaProps.type, form.type[0]) }, ...availableAddressTypesOptions],
      icon: form.type ? ADDRESS_TYPES[form.type[0]].icon : undefined,
      styles: {
        flexBasis: (visibleAddressesLength > 1 || item.parent) ? `66.6%` : undefined
      },
    }
  }];

  if (visibleAddressesLength > 1 || item.parent) {
    const options = [{ key: 'other', value: 'Inna' }, ...addedTypes.map((t: string) => ({ key: t, value: ADDRESS_TYPES[t as TAddressType].short_title }))];

    formGroupType.push({
      type: 'select',
      options: {
        placeholder: translate({ key: 'common.select' }),
        icon: undefined,
        name: 'same_is',
        label: form.same_is !== 'other' ? translate({ key: 'customer.contacts.same_address' }) : undefined,
        value: form.same_is,
        customValue: form.same_is !== 'other' ? ADDRESS_TYPES[form.same_is as 'legal' | 'postal' | 'registered' ].short_title : undefined,
        handleChange: (name, value) => handleChange(name as keyof TAddress['props'], value as string, item.id as string, form.type, item.parent),
        options: options,
        styles: {
          flexBasis: '33.3%'
        },
        notFoundText: translate({ key: 'common.nothing_found' }),
      }
    });
  }
  const formGroup: IFormGroupItem[][] = [formGroupType];

  const handleCityChange = (
    name: keyof TAddress['props'],
    value: string,
  ) => {
    if (value?.length >= MIN_VALUE_LENGTH_TO_START_SEARCH) {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        updateIsFetching(true);
        cancelTokenSource.cancel('Operation canceled by the user.');
        CancelToken = axios.CancelToken;
        cancelTokenSource = CancelToken.source();
        fetchCityData(form.zip_code, value, cancelTokenSource, (results) => {
          let addressMiscData: Record<string, string> = {};
          if (results.length) {
            addressMiscData = {
              city: results[0].city,
              region: results[0].province,
              county: results[0].county,
              post: results[0].post_office,
              commune: results[0].community,
            };
          }

          const touchedForm = {
            ...form,
            ...addressMiscData,
            [name]: value,
          };

          const streets = filterStreetsWithoutCity(results);

          if (streets.length > 1) {
            touchedForm.street = '';
            setStreets(streets, updateStreets);
          } else {
            touchedForm.street = streets?.length ? streets[0].street : touchedForm.city;
            updateStreets([]);
          }

          handleChangeMultiple(touchedForm, item.id);
          updateIsFetching(false);
        }, onError);
      }, SEARCH_DELAY);

    } else {
      const touchedForm = {
        ...form,
        [name]: value,
      };

      handleChangeMultiple(touchedForm, item.id);

      updateStreets([]);
    }

  };

  const setCityInput = () => ({
    type: cities.length > 1 && !isNotResident ? 'select' : 'input',
    options: {
      name: 'city',
      required: true,
      label: translateTitleInScheme(schemaProps.city),
      value: form.city,
      options: cities,
      searchable: true,
      applyCustomValueOnSearch: false,
      customValue: form.city,
      styles: {
        flexBasis: '66.6%'
      },
      placeholder: translate({ key: 'common.select' }),
      notFoundText: translate({ key: 'common.nothing_found' }),
      handleChange: (name: keyof TAddress['props'], value: string) => cities.length > 1 ? handleCityChange(name, value as string) : handleChange(name, value as string, item.id),
    }
  });

  const setStreetInput = () => ({
    type: streets.length > 1 && !isNotResident ? 'select' : 'input',
    options: {
      name: 'street',
      required: true,
      label: translateTitleInScheme(schemaProps.street),
      value: form.street,
      options: streets,
      searchable: true,
      fullStringSearch: true,
      applyCustomValueOnSearch: true,
      customValue: form.street,
      styles: {
        flexBasis: '66.6%'
      },
      placeholder: translate({ key: 'common.select' }),
      notFoundText: translate({ key: 'common.nothing_found' }),
      handleChange: (name: keyof TAddress['props'], value: string) => handleChange(name, value as string, item.id),
    }
  });

  const setAddressFromZip = (zipCodeResults: TZipCodeResult[]) => {
    handleChangeMultiple({
      ...form,
      ...fillAddressFieldsFromResults(zipCodeResults[0]),
    }, item.id);
  };

  if (item.props.same_is === 'other') {
    formGroup.push(
      [
        {
          type: 'input',
          options: {
            name: 'zip_code',
            label: translateTitleInScheme(schemaProps.zip_code),
            value: form.zip_code,
            placeholder: isNotResident ? '': '""-"""',
            styles: {
              flexBasis: `33.3%`
            },
            handleChange: (name: keyof TAddress['props'], value: string) => handleChange(name, value as string, item.id as string),
            onBlur: () => (form.zip_code?.length === 6 && !isNotResident) ? fetchZipCodeData(
              form.zip_code,
              setDefaultsAndUpdateZipCodeResults,
              updateCities,
              setAddressFromZip,
              updateStreets,
              onError
            ) : null
          }
        },
        setCityInput() as IFormGroupItem,
      ],

      [
        setStreetInput() as IFormGroupItem,
        {
          type: 'input',
          options: {
            name: 'house',
            label: translateTitleInScheme(schemaProps.house),
            value: form.house,
            className: 'address-house-nr',
            required: true,
            styles: {
              flexBasis: `${33.3 / 2}%`,
              borderRight: 'none',
            },
            handleChange: (name: keyof TAddress['props'], value: string) => handleChange(name, value as string, item.id as string),
          }
        },
        {
          type: 'input',
          options: {
            name: 'room',
            label: translateTitleInScheme(schemaProps.room),
            value: form.room,
            styles: {
              flexBasis: `${33.3 / 2}%`
            },
            handleChange: (name: keyof TAddress['props'], value: string) => handleChange(name, value as string, item.id as string),
          }
        },
      ]);
  }

  return (
    <FormGroup
      transparent={transparent}
      items={formGroup}
      disabled={isFetching || readOnly}
    />
  );
};

// Render Phones and Emails
const PhonesAndEmails = (
  contact: TCustomer['contacts'],
  handleSaveUserContact: (contact: TCustomer['contacts'], cb: (status: string, data?: Record<string, unknown>[]) => void) => void,
  onSaveContacts: (contacts: TCustomer['contacts']) => void,
  updateIsLoading: (isLoading: boolean) => void,
  customerContactsSchema: JSONSchemaProp,
  transparent?: boolean,
  isResident?: boolean,
  isClients?: boolean,
  handleDrawerClose?: () => void,
  readOnly?: boolean
) => {
  const [form, updateForm] = useState<TCustomer['contacts']>([]);

  const [isTouched, updateIsTouched] = useState(false);

  const phoneTemplate: TCustomerContact = {
    id: '',
    type: 'phone',
    subtype: 'mobile',
    value: '',
    main: false,
    error: []
  };

  const emailTemplate: TCustomerContact = {
    id: '',
    type: 'email',
    subtype: '',
    value: '',
    main: false,
    error: []
  };

  useEffect(() => {
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(contact)]);

  const resetForm = () => {
    contact ? updateForm(JSON.parse(JSON.stringify(contact))) : updateForm([]);
    updateIsTouched(false);
  };

  const handleSaveData = () => {
    updateIsLoading(true);
    const contactsRequiredFields: string[] = [];

    if (customerContactsSchema.required?.length) {
      contactsRequiredFields.push(...customerContactsSchema.required);
    }

    if (form?.length) {
      let touchedForm = [...form];
      let isErrors = false;

      touchedForm.forEach((contact, contactIndex) => {
        if (contact.deleted || !contact.touched) {
          return;
        }

        contactsRequiredFields.forEach((item) => {
          const props = customerContactsSchema.properties;
          let pattern, regExpErrorMessage;

          if (contact.type === 'email' && item === 'value') {
            const processedObj = customerEmailValidation();
            pattern = processedObj.pattern;
            regExpErrorMessage = processedObj.regExpErrorMessage;
          }

          if (contact.type === 'phone' && item === 'value') {
            const processedObj = customerPhoneValidation();
            pattern = processedObj.pattern;
            regExpErrorMessage = processedObj.regExpErrorMessage;
          }

          const rules = setRules({
            props,
            item,
            pattern,
            regExpErrorMessage
          });

          if (contact.type === 'email' && item === 'subtype') {
            return;
          }

          const errors = validate({
            field: item,
            parentField: 'contacts',
            listIndex: contactIndex,
            fieldName: props && props[item] ? translateTitleInScheme(props[item]) : undefined,
            value: contact[item as 'type' | 'subtype' | 'value'],
            rules,
            uncommonError: CUSTOMER_CONTACT_VALIDATION_FIELDS.includes(item)
          });

          if (errors?.length) {
            isErrors = true;
            touchedForm[contactIndex].error = errors;
          }
        });
      });

      if (isErrors) {
        updateIsTouched(true);
        updateForm(touchedForm);
        updateIsLoading(false);
        return;
      }
    }

    if (form?.length && isResident) {
      form.forEach(contact => {
        if (contact.type === 'phone' && contact.value && !contact.value.startsWith(window._env_.DEFAULT_COUNTRY_CODE)) {
          contact.value = addCountryCode(contact.value);
        }
      });
    }
    // updateIsTouched(false);
    handleSaveUserContact(form ? [...form] : [], (status, data) => {
      if (status !== 'ok') {
        updateIsLoading(false);
      }
      // @ts-ignore
      processResponse(status, data, form, updateForm, updateIsTouched, onSaveContacts, updateIsLoading, handleDrawerClose);
    });
  };

  const onAddItem = (type: 'phone' | 'email') => {
    const template = type === 'phone' ? { ...phoneTemplate } : { ...emailTemplate };
    const touchedForm = form ? [...form] : [];
    touchedForm.push({
      ...template,
      id: createTempId(),
      touched: true,
    });
    updateIsTouched(true);
    updateForm(touchedForm);
  };

  const handleChange = (name: keyof TCustomerContact, value: string | boolean, itemIndex: number) => {
    const touchedForm = form ? [...form] : [];
    if (touchedForm[itemIndex]) {
      touchedForm[itemIndex].error = [];
      touchedForm[itemIndex][name] = value as never;
      touchedForm[itemIndex].touched = true;
    }
    updateIsTouched(true);
    updateForm(touchedForm as TUserContact[]);
  };

  const onRemoveItem = (itemIndex: number) => {
    const touchedForm = form ? [...form] : [];
    if (touchedForm[itemIndex].id && !touchedForm[itemIndex].id?.includes('TEMP')) {
      touchedForm[itemIndex].deleted = true;
    } else {
      touchedForm.splice(itemIndex, 1);
    }

    // if Temp element was removed and no more elements left no need to call API
    updateIsTouched(!!touchedForm.length);
    updateForm(touchedForm);
  };

  return (
    <ContactsDataBlock isClients={isClients}>
      <ContactsDataBlockTitle>
        {translate({ key: 'customer.contacts.phone_and_email' })}
      </ContactsDataBlockTitle>
      <ContactsItemsWrapper>
        {renderContacts(form as TCustomerContact[], handleChange, onRemoveItem, transparent, readOnly)}
      </ContactsItemsWrapper>
      <ContactsControls flexPosition="start">
        <ButtonAddItem preset="secondary" onClick={() => onAddItem('phone')} disabled={readOnly}>
          <>
            {translate({ key: 'customer.contact.add_phone' })} <IconPlus icon="plus" />
          </>
        </ButtonAddItem>
        <ButtonAddItem preset="secondary" onClick={() => onAddItem('email')} disabled={readOnly}>
          <>
            {translate({ key: 'customer.contact.add_email' })} <IconPlus icon="plus" />
          </>
        </ButtonAddItem>
      </ContactsControls>
      {isTouched && renderControls(resetForm, handleSaveData, transparent, readOnly)}
    </ContactsDataBlock>
  );
};

const renderContacts = (
  items: TUserContact[],
  handleChange: (name: keyof TCustomerContact, value: string | boolean, index: number) => void,
  onRemoveItem: (index: number) => void,
  transparent?: boolean,
  readOnly?: boolean,
) => items.map((item, index) => (
  item.deleted ? (item.error?.length ? renderErrors(item.error) : null) : <ContactsItemWrapper key={`contact-${index}`}>
    <ContactsItem>
      <FormGroup
        transparent={transparent}
        items={[
          createContactFormFields(item, index, handleChange, transparent)
        ]}
        disabled={readOnly}
      />
      {item.error?.length ? renderErrors(item.error) : null}
    </ContactsItem>
    {!readOnly && <ContactsRemoveIcon icon="trash" onClick={() => onRemoveItem(index)} />}
  </ContactsItemWrapper>
));

export const ContactPersons = (
  persons: TCustomer['contact_persons'],
  handleSaveUserContactPersons: (persons: TContactPerson[], cb: (status: string, data?: Record<string, unknown>[]) => void) => void,
  onSavePersons: (persons: TCustomer['contact_persons']) => void,
  schema: JSONSchemaProp,
  updateIsLoading: (isLoading: boolean) => void,
  transparent?: boolean,
  isClients?: boolean,
  handleDrawerClose?: () => void,
  readOnly = false,
) => {
  const [form, updateForm] = useState<TContactPerson[]>([]);
  const [isTouched, updateIsTouched] = useState(false);
  const contactPersonTemplate = {
    preferred: false,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  };

  useEffect(() => {
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CONTACT_PERSONS_SCHEMA = schema;

  const resetForm = () => {
    if (persons?.length) {

      const preparedPersons = persons.map(item => ({
        id: item.id,
        preferred: item.preferred,
        ...item.props,
      }));

      updateForm(JSON.parse(JSON.stringify(preparedPersons)));
    } else {
      updateForm([]);
    }

    updateIsTouched(false);
  };

  const handleChange = (name: keyof TContactPerson, value: string | boolean, itemIndex: number) => {
    let touchedForm = [...form];
    if (name === 'preferred' && value === true) {
      touchedForm = touchedForm.map(item => ({ ...item, preferred: false }));
    }
    touchedForm[itemIndex].error = [];
    touchedForm[itemIndex][name] = value as never;
    updateIsTouched(true);
    updateForm(touchedForm);
  };

  const onAddContactPerson = () => {
    const touchedForm = [...form];
    touchedForm.push({
      ...contactPersonTemplate,
      id: createTempId(),
    });
    updateIsTouched(true);
    updateForm(touchedForm);
  };

  const onRemoveContactPerson = (index: number) => {
    const touchedForm = [...form];
    if (touchedForm[index].id && !touchedForm[index].id?.includes('TEMP')) {
      touchedForm[index].deleted = true;
    } else {
      touchedForm.splice(index, 1);
    }

    updateIsTouched(!!touchedForm.length);
    updateForm(touchedForm);
  };

  const updateUser = (persons: TContactPerson[]) => {
    const processedPersons = persons.map(contact => (
      {
        id: contact.id,
        preferred: contact.preferred,
        props: {
          first_name: contact.first_name,
          last_name: contact.last_name,
          email: contact.email,
          phone: contact.phone,
        },
      }
    ));

    onSavePersons(processedPersons);
  };

  const handleSaveData = () => {
    //@TODO: add validation
    updateIsLoading(true);
    handleSaveUserContactPersons([...form], (status, data) => {
      if (status !== 'ok') {
        updateIsLoading(false);
      }
      // @ts-ignore
      processResponse(status, data, form, updateForm, updateIsTouched, updateUser, updateIsLoading, handleDrawerClose);
    });
  };

  return (
    <ContactsDataBlock isClients={isClients}>
      <ContactsDataBlockTitle>
        {translate({ key: 'customer.contact.contact_person' })}
      </ContactsDataBlockTitle>
      <ContactsItemsWrapper>
        {form && form.map((item, index) => item.deleted ? (item.error?.length && index === 0 ? renderErrors(item.error) : null) : <ContactPerson item={item} index={index} handleChange={handleChange} onRemoveItem={onRemoveContactPerson} contactPersonSchema={CONTACT_PERSONS_SCHEMA} readOnly={readOnly} preferredByDefault={true} />)}
      </ContactsItemsWrapper>
      <ContactsControls flexPosition="start">
        <ButtonAddItem preset="secondary" onClick={onAddContactPerson} disabled={readOnly}>
          <>
            {translate({ key: 'customer.contact.add_contact_person' })} <IconPlus icon="plus" />
          </>
        </ButtonAddItem>
      </ContactsControls>
      {isTouched && renderControls(resetForm, handleSaveData, transparent, readOnly)}
    </ContactsDataBlock>
  );

};

export const ContactPerson = ({ item, index, handleChange, onRemoveItem, contactPersonSchema, readOnly, errors, preferredByDefault }: {
  item: TContactPerson,
  index: number,
  handleChange: (name: keyof TContactPerson, value: string | boolean, index: number) => void,
  onRemoveItem?: (index: number) => void,
  contactPersonSchema: JSONSchemaProp,
  readOnly?: boolean,
  errors?: TError[],
  preferredByDefault?: boolean,
}) => {
  const contactPersonSchemaProps = contactPersonSchema.properties?.props?.properties as JSONSchema;

  return (
    <ContactsItemWrapper key={`contact-person-${index}`} bordered={true}>
      <ContactsItem>
        <ContactPersonRow gridTemplate="1fr 1fr">
          <Input
            label={translateTitleInScheme(contactPersonSchemaProps.first_name)}
            name="first_name"
            value={item.first_name}
            handleChange={(name, value) => handleChange(name as keyof TContactPerson, value as string, index)}
            disabled={readOnly}
            error={errors?.find(err => err.parentField === 'contact_persons' && err.listIndex === index && err.field === `first_name`)?.message}
          />
          <Input
            label={translateTitleInScheme(contactPersonSchemaProps.last_name)}
            name="last_name"
            value={item.last_name}
            handleChange={(name, value) => handleChange(name as keyof TContactPerson, value as string, index)}
            disabled={readOnly}
            error={errors?.find(err => err.parentField === 'contact_persons' && err.listIndex === index && err.field === `last_name`)?.message}
          />
        </ContactPersonRow>
        <ContactPersonRow gridTemplate={preferredByDefault ? '1fr 1fr' : '33% 1fr 1fr' }>
          <Input
            label={translateTitleInScheme(contactPersonSchemaProps.phone)}
            name="phone"
            value={item.phone}
            handleChange={(name, value) => handleChange(name as keyof TContactPerson, value as string, index)}
            disabled={readOnly}
            placeholder={translate({ key: 'common.contact.phone_placeholder' })}
            error={errors?.find(err => err.parentField === 'contact_persons' && err.listIndex === index && err.field === `phone`)?.message}
          />
          <Input
            label={translateTitleInScheme(contactPersonSchemaProps.email)}
            name="email"
            value={item.email}
            handleChange={(name, value) => handleChange(name as keyof TContactPerson, value as string, index)}
            disabled={readOnly}
            error={errors?.find(err => err.parentField === 'contact_persons' && err.listIndex === index && err.field === `email`)?.message}
          />
          {!preferredByDefault && (
            <InputOption
              label={translateTitleInScheme(contactPersonSchema.properties?.preferred as JSONSchemaProp)}
              name="preferred"
              active={item.preferred}
              handleChange={(name, value) => handleChange(name as keyof TContactPerson, value as boolean, index)}
              disabled={readOnly}
            />
          )}
        </ContactPersonRow>
        {item.error?.length ? renderErrors(item.error) : null}
      </ContactsItem>
      {(!readOnly && onRemoveItem) && (<ContactsRemoveIcon icon="trash" onClick={() => onRemoveItem(index)} />)}
    </ContactsItemWrapper>
  );
};

const processResponse = (
  status: string,
  data: { id: string, status: number, data?: Record<string, unknown>, error: AxiosError }[],
  form: TAddress[],
  updateForm: (form: Record<string, unknown>[]) => void,
  updateIsTouched: (touched: boolean) => void,
  updateUser: (form: Record<string, unknown>[]) => void,
  updateIsLoading: (isLoading: boolean) => void,
  handleDrawerClose?: () => void,
  handleRevisionError?: () => void,
) => {
  switch (status) {
    case 'ok':
      if (data) {
        const { hasError, touchedForm } = processResponseData(data, form);

        if (handleRevisionError && touchedForm.some(item => item.error?.length && item.error?.some(error => error.code === REVISION_ERROR_CODE))) {
          handleRevisionError();
        } else {
          updateForm(touchedForm);

          if (!hasError) {
            updateUser(touchedForm);
            updateIsTouched(false);
            handleDrawerClose && handleDrawerClose();
          } else {
            updateIsLoading(false);
          }
        }
      }
      break;
    case 'error':
      console.error(data);
      break;
  }
};

const processResponseData = (
  data?: { id: string, status: number, data?: Record<string, unknown>, error: AxiosError }[],
  form?: TAddress[],
) => {
  let hasError = false;
  let touchedForm = form ? [...form] : [];

  data && data.forEach((item) => {
    const itemIndex = touchedForm.findIndex(i => i.id === item.id);

    if (itemIndex !== -1) {
      const touchedItem = touchedForm[itemIndex];

      if (item.error) {
        hasError = true;
        const error = item.error as AxiosError;

        if (error) {
          touchedForm[itemIndex].error = processResponseErrors(error);
        } else {
          touchedForm[itemIndex].error = [{ code: '', message: 'Undefined Error' }];
        }
      } else if (item.data && item.data?.id) {
        touchedForm[itemIndex].id = item.data.id as string;
      } else if (touchedItem.deleted && item.status === 204) {
        touchedForm.splice(itemIndex, 1);
      }
    }
  });

  return {
    hasError,
    touchedForm
  };
};

export default Contacts;
